import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { shouldHideFooterForProductKind } from './PageLayoutConfig';

type PageLayoutContextValue = {
  hideFooter: boolean;
  setHideFooter: (hide: boolean) => void;
};

const PageLayoutContext = createContext<PageLayoutContextValue | null>(null);

export function PageLayoutProvider({ children }: { children: ReactNode }) {
  const [hideFooter, setHideFooter] = useState(false);
  const value = useMemo(
    () => ({ hideFooter, setHideFooter }),
    [hideFooter]
  );
  return (
    <PageLayoutContext.Provider value={value}>{children}</PageLayoutContext.Provider>
  );
}

export function usePageLayoutContext(): PageLayoutContextValue {
  const ctx = useContext(PageLayoutContext);
  if (!ctx) {
    throw new Error('usePageLayoutContext must be used within PageLayoutProvider');
  }
  return ctx;
}

/** 按 productKind 同步全站壳层（底栏、视口滚动锁等），卸载时自动恢复 */
export function PageLayoutBridge({
  productKind,
}: {
  productKind?: string | null;
}) {
  const { setHideFooter } = usePageLayoutContext();

  useEffect(() => {
    const hide = shouldHideFooterForProductKind(productKind);
    setHideFooter(hide);
    return () => setHideFooter(false);
  }, [productKind, setHideFooter]);

  useEffect(() => {
    const immersive = shouldHideFooterForProductKind(productKind);
    const root = document.documentElement;
    if (immersive) {
      root.classList.add('lf-immersive-page');
    } else {
      root.classList.remove('lf-immersive-page');
    }
    return () => root.classList.remove('lf-immersive-page');
  }, [productKind]);

  return null;
}
